import { useEffect, useState } from 'react';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { fetchCurrentUser } from './api/auth';

const TOKEN_STORAGE_KEY = 'access_token';

function App() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_STORAGE_KEY));
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(token));
  const [sessionError, setSessionError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!token) {
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    setSessionError(null);
    fetchCurrentUser(token)
      .then((currentUser) => {
        if (!cancelled) setUser(currentUser);
      })
      .catch((err) => {
        if (cancelled) return;
        // A saved token is only ever cleared when the server itself rejects it
        // (401/403). A network hiccup while validating it on load must not sign
        // the admin out of a perfectly valid session — show a retryable error
        // instead and keep the token intact.
        if (err.status === 401 || err.status === 403) {
          localStorage.removeItem(TOKEN_STORAGE_KEY);
          setToken(null);
        } else {
          setSessionError(err.message);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, retryKey]);

  const handleLoginSuccess = (newToken) => {
    localStorage.setItem(TOKEN_STORAGE_KEY, newToken);
    setToken(newToken);
    setIsLoading(true);
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
    setUser(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-sm text-gray-500">Loading...</p>
      </div>
    );
  }

  if (sessionError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center space-y-4">
          <p className="text-sm text-red-600">{sessionError}</p>
          <button
            onClick={() => setRetryKey((key) => key + 1)}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-sm bg-white shadow-sm border border-gray-200 rounded-lg p-6 text-center space-y-4">
          <p className="text-gray-900 font-medium">This dashboard is for admin users only.</p>
          <button
            onClick={handleLogout}
            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Log out
          </button>
        </div>
      </div>
    );
  }

  return <Dashboard token={token} user={user} onLogout={handleLogout} />;
}

export default App;
